import { signal } from '@preact/signals';

// ============================================================================
// Camera - Manages viewport state and coordinate transforms
// ============================================================================

export interface CameraState {
  readonly x: number;
  readonly y: number;
  readonly zoom: number;
}

// Mutable version for internal double-buffer use
interface CameraMutable {
  x: number;
  y: number;
  zoom: number;
}

// Camera constants
export const CAMERA_SPEED = 800;
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 4;

// Cap camera movement delta so occasional long frames don't cause large jumps.
const MAX_CAMERA_DELTA_MS = 34;

// ============================================================================
// PERFORMANCE: Double-buffered camera state
// ============================================================================
// Preact signals trigger subscribers when .value is assigned a new reference
// (Object.is check). We use two preallocated camera objects and alternate
// between them so each assignment gives the signal a different reference
// without any object allocation.
// ============================================================================

const _cameraA: CameraMutable = { x: 0, y: 0, zoom: 2 };
const _cameraB: CameraMutable = { x: 0, y: 0, zoom: 2 };
let _cameraActive: CameraMutable = _cameraA;

const cameraSignal = signal<CameraState>(_cameraA);

// Copy current camera into the inactive buffer, switch to it, and return it for mutation.
// Caller must set fields on the returned object, then call _commitCamera().
const _swapBuffer = (): CameraMutable => {
  const cur = _cameraActive;
  const next = cur === _cameraA ? _cameraB : _cameraA;
  next.x = cur.x;
  next.y = cur.y;
  next.zoom = cur.zoom;
  _cameraActive = next;
  return next;
};

const _commitCamera = ({ cam }: { cam: CameraMutable }): void => {
  cameraSignal.value = cam;
};

// Screen dimensions
let screenWidth = 0;
let screenHeight = 0;

// ============================================================================
// PERFORMANCE: Preallocated reusable objects to avoid GC pressure
// ============================================================================

const _tempWorldPoint = { x: 0, y: 0 };
const _tempScreenPoint = { x: 0, y: 0 };
const _screenDims = { width: 0, height: 0 };

// ============================================================================
// Initialization
// ============================================================================

export const initCamera = ({ width, height }: { width: number; height: number }): void => {
  screenWidth = width;
  screenHeight = height;
  _screenDims.width = width;
  _screenDims.height = height;
};

export const resizeCamera = ({ width, height }: { width: number; height: number }): void => {
  screenWidth = width;
  screenHeight = height;
  _screenDims.width = width;
  _screenDims.height = height;
};

// ============================================================================
// Camera State
// ============================================================================

export const getCamera = (): Readonly<CameraState> => cameraSignal.value;

export const getCameraSignal = () => cameraSignal;

// PERFORMANCE: Returns preallocated object - caller must use values immediately
export const getScreenDimensions = (): Readonly<{ width: number; height: number }> => _screenDims;

// ============================================================================
// Camera Movement
// ============================================================================

export const updateCameraFromInput = ({ flags, delta }: { flags: number; delta: number }): void => {
  const camera = cameraSignal.value;
  const clampedDelta = Math.min(delta, MAX_CAMERA_DELTA_MS);
  const speed = (CAMERA_SPEED * clampedDelta) / 1000 / camera.zoom;
  let dirX = 0;
  let dirY = 0;

  if (flags & 1) {
    dirY -= 1;
  }
  if (flags & 2) {
    dirY += 1;
  }
  if (flags & 4) {
    dirX -= 1;
  }
  if (flags & 8) {
    dirX += 1;
  }

  if (dirX === 0 && dirY === 0) {
    return;
  }

  // Keep camera speed constant while moving diagonally.
  const axisScale = dirX !== 0 && dirY !== 0 ? Math.SQRT1_2 : 1;
  const x = camera.x + dirX * speed * axisScale;
  const y = camera.y + dirY * speed * axisScale;

  if (x !== camera.x || y !== camera.y) {
    // PERFORMANCE: Double-buffer swap instead of spread allocation
    const next = _swapBuffer();
    next.x = x;
    next.y = y;
    _commitCamera({ cam: next });
  }
};

export const centerOnPosition = ({ x, y }: { x: number; y: number }): void => {
  const next = _swapBuffer();
  next.x = x;
  next.y = y;
  _commitCamera({ cam: next });
};

export const centerOnPositionWithZoom = ({
  x,
  y,
  zoom,
}: {
  x: number;
  y: number;
  zoom: number;
}): void => {
  const clampedZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom));
  const next = _swapBuffer();
  next.x = x;
  next.y = y;
  next.zoom = clampedZoom;
  _commitCamera({ cam: next });
};

export const setZoom = ({ zoom }: { zoom: number }): void => {
  const clampedZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom));
  const next = _swapBuffer();
  next.zoom = clampedZoom;
  _commitCamera({ cam: next });
};

export const panCamera = ({
  screenDeltaX,
  screenDeltaY,
}: {
  screenDeltaX: number;
  screenDeltaY: number;
}): void => {
  const camera = cameraSignal.value;
  // Convert screen delta to world delta (inverse of zoom)
  const worldDeltaX = screenDeltaX / camera.zoom;
  const worldDeltaY = screenDeltaY / camera.zoom;
  const next = _swapBuffer();
  next.x = camera.x - worldDeltaX;
  next.y = camera.y - worldDeltaY;
  _commitCamera({ cam: next });
};

export const zoomAtPoint = ({
  zoomDelta,
  screenX,
  screenY,
}: {
  zoomDelta: number;
  screenX: number;
  screenY: number;
}): void => {
  const camera = cameraSignal.value;

  // Get world position under cursor before zoom
  const worldBeforeX = (screenX - screenWidth / 2) / camera.zoom + camera.x;
  const worldBeforeY = (screenY - screenHeight / 2) / camera.zoom + camera.y;

  // Calculate new zoom level with smooth scaling
  const zoomFactor = 1 - zoomDelta * 0.001;
  const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, camera.zoom * zoomFactor));

  // Get world position under cursor after zoom
  const worldAfterX = (screenX - screenWidth / 2) / newZoom + camera.x;
  const worldAfterY = (screenY - screenHeight / 2) / newZoom + camera.y;

  // Adjust camera to keep the same world point under cursor
  const next = _swapBuffer();
  next.x = camera.x + (worldBeforeX - worldAfterX);
  next.y = camera.y + (worldBeforeY - worldAfterY);
  next.zoom = newZoom;
  _commitCamera({ cam: next });
};

// ============================================================================
// Coordinate Transforms
// ============================================================================

// PERFORMANCE: Uses preallocated object - caller must use values immediately
export const screenToWorld = ({
  screenX,
  screenY,
}: {
  screenX: number;
  screenY: number;
}): Readonly<{ x: number; y: number }> => {
  const camera = cameraSignal.value;
  _tempWorldPoint.x = (screenX - screenWidth / 2) / camera.zoom + camera.x;
  _tempWorldPoint.y = (screenY - screenHeight / 2) / camera.zoom + camera.y;
  return _tempWorldPoint;
};

// PERFORMANCE: Uses preallocated object - caller must use values immediately
export const worldToScreen = ({
  worldX,
  worldY,
}: {
  worldX: number;
  worldY: number;
}): Readonly<{ x: number; y: number }> => {
  const camera = cameraSignal.value;
  _tempScreenPoint.x = (worldX - camera.x) * camera.zoom + screenWidth / 2;
  _tempScreenPoint.y = (worldY - camera.y) * camera.zoom + screenHeight / 2;
  return _tempScreenPoint;
};

// ============================================================================
// Viewport Calculations
// ============================================================================

export const getViewportBounds = (): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
} => {
  const camera = cameraSignal.value;
  const halfWidth = screenWidth / 2 / camera.zoom;
  const halfHeight = screenHeight / 2 / camera.zoom;

  return {
    minX: camera.x - halfWidth,
    maxX: camera.x + halfWidth,
    minY: camera.y - halfHeight,
    maxY: camera.y + halfHeight,
  };
};
